import { Order } from 'shared/types/cart';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import OrderCosts from 'components/gruenland/organisms/order-costs/OrderCosts';
import OrderItemsList from 'components/gruenland/organisms/order-items-listing/OrderItemsList';
import { useFormat } from 'helpers/hooks/useFormat';
import { TYPOGRAPHY_PRIVATE_AREA_TITLE } from 'lib/typography';
import { ShippingMethod } from 'types/entity/cart';
import OrderDetails from './OrderDetails';
import OrderShippingDetails from './OrderShippingDetails';
import useOrderData from '../helper-hooks/useOrderData';

export interface Props {
  order: Order;
  shippingMethods: ShippingMethod[];
}

const OrderPage: React.FC<Props> = ({ order, shippingMethods }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });
  const { shippingInfo } = useOrderData({
    order,
    shippingMethods,
  });

  return (
    order && (
      <>
        <Typography as="h2" className={TYPOGRAPHY_PRIVATE_AREA_TITLE}>
          {formatMessage({
            id: 'order.details',
            defaultMessage: 'Dettagli ordine',
          })}
        </Typography>
        <div className="mt-6 flex flex-col gap-4 xl:flex-row xl:gap-20">
          <div className="xl:w-1/2">
            <OrderDetails order={order} />
            <OrderShippingDetails order={order} shippingInfo={shippingInfo} />
          </div>
          <div className="xl:w-1/2">
            <OrderCosts order={order} className="bg-neutrals-gray-light p-2" />

            {order.invoiceUrl && (
              <Link link={order.invoiceUrl} target="_blank" variant="buttonPrimaryGreen" className="mt-2">
                <Typography className="text-center text-[14px] md:text-[16px]">
                  {formatMessage({
                    id: 'order.invoice',
                    defaultMessage: 'Fattura',
                  })}
                </Typography>
              </Link>
            )}
          </div>
        </div>
        <div>
          <OrderItemsList className="my-6 border-y" lineItems={order.lineItems || []} />
        </div>

        {/* Returns section */}
        {order.returnInfo && order.returnInfo.length > 0 && (
          <div className="mt-8">
            <Typography as="h3" className="mb-4 text-[18px] font-medium text-primary-black">
              {formatMessage({ id: 'order.returns', defaultMessage: 'Resi' })}
            </Typography>
            <div className="space-y-4">
              {order.returnInfo.map((returnItem, index) => (
                <div key={index} className="rounded-sm border border-neutral-200 bg-neutral-50 p-4">
                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <Typography className="text-[14px] font-medium text-secondary-black">
                        {`${formatMessage({ id: 'order.returnDate', defaultMessage: 'Data reso' })}:`}
                      </Typography>
                      <Typography className="text-[14px] text-primary-black">
                        {returnItem.returnDate ? new Date(returnItem.returnDate).toLocaleDateString() : '-'}
                      </Typography>
                    </div>
                    <div>
                      <Typography className="text-[14px] font-medium text-secondary-black">
                        {`${formatMessage({ id: 'order.returnTracking', defaultMessage: 'Tracking reso' })}:`}
                      </Typography>
                      <Typography className="text-[14px] text-primary-black">
                        {returnItem.returnTrackingId || '-'}
                      </Typography>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Typography className="text-[14px] font-medium text-secondary-black">
                      {`${formatMessage({ id: 'order.returnItems', defaultMessage: 'Articoli in reso' })}:`}
                    </Typography>
                    <Typography className="text-[14px] text-primary-black">
                      {`${returnItem.lineItems?.length || 0} articoli`}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    )
  );
};

export default OrderPage;
